/**
 * Rate Card — Medicare lookup sub-section (contracts-plan.md §7.6, §11 tab 3;
 * §13.5 WP-43; R18, decision D13).
 *
 * §7.6, verbatim: "A section within the Rate Card tab: look up a CPT or HCPCS
 * code and see the CMS Physician Fee Schedule non-facility and facility
 * amounts for the North Carolina locality for the selected year, beside the
 * card's Low, Preferred and High and the entry's datapoints."
 *
 * So: a code box, a year selector, one row per imported locality with the two
 * amounts, and — beside them — the card's own numbers for every master entry
 * that carries that CPT or HCPCS code. The backend does the join (one call,
 * `GET /medicare/lookup`); this file renders it.
 *
 * PROPS CONTRACT (what CardView.tsx passes at the mount step — see
 * backend/src/routes/contracts/medicare.mount.md §5). Every prop is optional,
 * so `<MedicareSection />` renders on its own:
 *   scope              'network' | 'site'; default 'site'
 *   siteId             the card's selected site, or null for company-wide
 *   therapeuticAreaId  the card's area toggle, or null for overall
 * These exist so the Low / Preferred / High shown beside the CMS amounts are
 * the SAME numbers the card above is showing (§7.2) — a lookup that silently
 * answered from a different scope would put two different "Preferred" values
 * on one screen.
 *
 * G7 and G8: every amount carries its label ('derived' — computed from CMS's
 * published RVUs, GPCIs and conversion factor, never read off a price list)
 * and its provenance (the release URL and the moment it was fetched) in the
 * title attribute, which is where §11 puts provenance.
 *
 * Design: .claude/skills/siteoperations-design/SKILL.md v2 — shared primitives
 * from ui/, primary blue only, dark classes on every surface.
 */
import { useCallback, useState } from 'react';
import { Landmark } from 'lucide-react';

import { SearchInput, FilterSelect, primaryBtnCls } from '../../../../ui';
import { lookupMedicare } from '../../../api/medicare';
import type { MedicareLookupResult } from '../../../api/medicare';
import type { RateCardEntryRow, RcScope } from '../../../api/rateCard';

export interface MedicareSectionProps {
  scope?: RcScope;
  siteId?: string | null;
  therapeuticAreaId?: string | null;
}

/** The years offered: this year and the four before it, newest first. */
function yearOptions(): Array<{ value: string; label: string }> {
  const current = new Date().getUTCFullYear();
  const years = [0, 1, 2, 3, 4].map((back) => String(current - back));
  return [{ value: '', label: 'Configured year' }, ...years.map((y) => ({ value: y, label: y }))];
}

/** Money, or an em dash when CMS publishes no amount for that setting. */
function money(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** A card number, or an em dash when the entry has no accepted datapoint yet (§7.2). */
function cardNumber(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CardValues({ row }: { row: RateCardEntryRow }) {
  return (
    <tr className="text-sm">
      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
        {row.official_name}
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{row.kind}</span>
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300" data-testid={`medicare-card-low-${row.entry_id}`}>
        {cardNumber(row.low)}
      </td>
      <td
        className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100"
        data-testid={`medicare-card-preferred-${row.entry_id}`}
      >
        {cardNumber(row.preferred)}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300" data-testid={`medicare-card-high-${row.entry_id}`}>
        {cardNumber(row.high)}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-500 dark:text-gray-400">{row.count}</td>
      <td className="px-3 py-2 text-gray-500 dark:text-gray-400" data-testid={`medicare-card-datapoints-${row.entry_id}`}>
        {row.datapoint_ids.length}
      </td>
    </tr>
  );
}

export function MedicareSection({ scope = 'site', siteId = null, therapeuticAreaId = null }: MedicareSectionProps) {
  const [code, setCode] = useState('');
  const [year, setYear] = useState('');
  const [result, setResult] = useState<MedicareLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(() => {
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      setError('Enter a CPT or HCPCS code.');
      setResult(null);
      return;
    }
    setBusy(true);
    lookupMedicare({
      code: trimmed,
      year: year === '' ? undefined : year,
      scope,
      siteId: scope === 'site' ? siteId : null,
      therapeuticAreaId,
    })
      .then((found) => {
        setResult(found);
        setError(null);
      })
      .catch((err: unknown) => {
        const body = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setResult(null);
        setError(body ?? `No Medicare rate found for ${trimmed}.`);
      })
      .finally(() => setBusy(false));
  }, [code, year, scope, siteId, therapeuticAreaId]);

  const cardRows: RateCardEntryRow[] = (result?.entries ?? []).flatMap((entry) => entry.values);

  return (
    <div
      className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
      data-testid="medicare-section"
    >
      <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
        <Landmark className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
        Medicare lookup
      </h4>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        CMS Physician Fee Schedule non-facility and facility amounts for the imported localities, beside the card&rsquo;s
        Low, Preferred and High (§7.6). Amounts are derived from the published RVUs, GPCIs and conversion factor.
      </p>

      <form
        className="mt-3 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <div className="min-w-0 flex-1 basis-44" data-testid="medicare-code-field">
          <SearchInput
            value={code}
            onChange={setCode}
            label="CPT or HCPCS code"
            placeholder="CPT or HCPCS code, e.g. 93000"
          />
        </div>
        <FilterSelect label="Year" auto value={year} onChange={setYear} options={yearOptions()} />
        {/* type="button" with an explicit handler, not type="submit": jsdom has
            no form submission, and the enclosing form's onSubmit already
            carries the Enter key. */}
        <button type="button" onClick={run} className={primaryBtnCls} disabled={busy} data-testid="medicare-submit">
          {busy ? 'Looking up…' : 'Look up'}
        </button>
      </form>

      {error !== null && (
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300" data-testid="medicare-error">
          {error}
        </p>
      )}

      {result !== null && (
        <div className="mt-4 space-y-4">
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  {['Locality', 'Modifier', 'Non-facility', 'Facility'].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {result.localities.map((rate) => {
                  const provenance = `${rate.label} — CMS Physician Fee Schedule ${rate.year}, ${rate.source_url}, fetched ${rate.fetched_at}`;
                  const testKey = `${rate.locality_code}${rate.modifier ? `-${rate.modifier}` : ''}`;
                  return (
                    <tr key={rate.id} className="text-sm" data-testid={`medicare-locality-${testKey}`}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        {rate.locality_name}
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{rate.locality_code}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{rate.modifier ?? '—'}</td>
                      <td
                        className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100"
                        title={provenance}
                        data-testid={`medicare-nonfacility-${testKey}`}
                      >
                        {money(rate.non_facility_rate)}
                      </td>
                      <td
                        className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100"
                        title={provenance}
                        data-testid={`medicare-facility-${testKey}`}
                      >
                        {money(rate.facility_rate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div>
            <h5 className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Beside the card
            </h5>
            {cardRows.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="medicare-no-card-entry">
                No rate-card entry carries code {result.code} yet, so there is nothing to compare. Add the code to the
                entry in Admin &rsaquo; Master lists (§7.5).
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      {['Entry', 'Low', 'Preferred', 'High', 'Count', 'Datapoints'].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {cardRows.map((row) => (
                      <CardValues key={`${row.entry_id}-${row.kind}`} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MedicareSection;
